import { ICalendarSetting } from '@app-types/websocket/user.types';
import { IArticleTarget } from '@app-types/article.types';
import AddFamilyMemberDialog from '@components/extended-family/add-child-dialog/addFamilyMember';
import FamilyList from '@components/extended-family/family-list/FamilyList';
import InviteParentDialog from '@components/extended-family/InviteParentDialog';
import {FormControlLabel, MenuItem, Switch, Typography, WithStyles, withStyles} from '@material-ui/core';
import Button from "@components/common/button/Button";
import Select from '@components/common/select/Select';
import IconButton from '@components/common/iconButton/iconButton.component';
import HelpOutline from '@material-ui/icons/HelpOutline';
import GetApp from '@material-ui/icons/GetApp';
import { openArticle } from '@services/article/article.actions';
import * as CallsActions from '@services/calls/calls.actions';
import * as UserActions from '@services/user/user.actions';
import * as ChatActions from '@services/chat/chat.actions';
import * as FamilyActions from '@services/family/family.actions';
import * as React from 'react';
import { connect } from 'react-redux';
import UpdateFamilyMemberDialog from '@components/extended-family/update-family-member/UpdateFamilyMemberDialog';
import { IFamilyData, IFamilyMember, IFamilyMemberFileIdUpdate } from '@app-types/websocket/family.types';
import ExportEvents from '@components/calendar-export/export.calendar';
import SelectLanguage from '@components/common/language/SelectLanguage';
import UpdatePasswordDialog from '@components/settings/update-password.component';
import TranslateNew from '@translate/TranslateNew';
import { FamilyMemberType } from '@services/family/family.constants';
import FamilySwitch from '@components/common/familySwitch/familyswitch.component';
import NewFamilyDialog from '@components/extended-family/NewFamilyDialog';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { OffSvg } from '@images/OffSvg';
import { LockSvg } from '@images/LockSvg';
import { EmailSvg } from '@images/email';
import { LanguageSvg } from '@images/LanguageSvg';
import SubscriptionSettings from '@components/subscription/SubscriptionSettings';
import EraseAccountDialog from '@components/settings/eraseaccount.component';
import ConfirmSettingDialog from '@components/settings/confirm-setting.component';
import BottomBar from '@components/calendar2/fab/BottomBar';
import { getFamilyColors, isGroupOnlyMine } from '@services/family/family.utils';
import ShareEventDialog from '@components/settings/share-events.component';
import UpdateEmailDialog from '@components/settings/update-email.component';
import AddUserDialog from '@components/extended-family/add-child-dialog/AddUserDialog';
import CoppaInviteBlockedDialog from '@components/extended-family/CoppaInviteBlockedDialog';
import { isCoppaBlockedInvite } from '@services/family/coppa';
import {SettingsContainerStyle as styles} from './settings.style';
import EmailIntegrationSettings, { IEmailIntegrationSettingsCopy } from '@components/integrations/email/EmailIntegrationSettings';
import { getForwardingEmailAddress, getSelectedFamilyResource } from '@services/user/user.utils';
import { TOXIC_CHECK_LEVEL, toxicCheckLevelOf } from '@services/user/toxicCheckLevel';
import { previewsDisabledFor } from '@services/chat/chatPreview';
import { saveEconomy } from '@services/economy/economy.websocket';
import { saveTodos } from '@services/todos/todo.websocket';
import { ChatType, IChat } from '@app-types/websocket/chat.types';

const tn: TranslateNew = TranslateNew.getInstance();
const SHOW_EMAIL_SETTINGS = true;

interface IProps extends RouteComponentProps, WithStyles<typeof styles> {
  session: any;
  userRole: FamilyMemberType;
  userId: number;
  family: IFamilyMember[];
  extendedFamily: IFamilyMember[];
  resources: any[];
  calendarSettings: ICalendarSetting;
  toxicCheckLevel:number;
  autoApproveSuggestions:boolean;
  disablePreviews:boolean;
  scheduleChatId?: number;
  isOnline: boolean;
  enhancedVisibility:boolean;
  darkMode:boolean|undefined; // true always dark, false always light, undefined follows the phone
  language:string;
  updateFamilyMember: (data: IFamilyMember) => void;
  deleteFamilyMember: (id: number) => void;
  unlimitFamilyMember: (id: number) => void;
  restoreAccount: (id: number) => void;
  nudgeToLogIn: (id: number) => void;
  createFamily: (family: IFamilyData) => void;
  changePassword: (data: string) => void;
  updateEmail: (data: string) => void;
  eraseAccount: () => void;
  logout: () => void;
  selectFamily: (index: number) => void;
  deleteGroup: (data: UserActions.IDeleteGroup) => void;
  clearChat: (data:any) => void;
  reloadResources: () => void;
  activateEmailAddress: (data: UserActions.IEmailAddressActivation) => void;
  emailActivation?: UserActions.IEmailAddressActivation & {pending: boolean; failed: boolean};
  allowCalls: (allow:boolean) => void;
  updateCalendarSettings: (data: ICalendarSetting) => void;
  updateFamilyMemberFileId: (data:IFamilyMemberFileIdUpdate) => void;
  updateFamilyColors: (colors:string[]) => void;
  updateToxicCheckLevel: (level:number) => void;
  setDisablePreviews: (data:UserActions.IDisablePreviews) => void;
  setAutoApproveSuggestions: (data: UserActions.IAutoApproveSuggestions) => void;
  saveChat: (chatId: number) => void;
  displaySuccessMessage: (message: string) => void;
  displayErrorMessage: (message: string) => void;
  openArticle: (target: IArticleTarget) => void;
  setEnhancedVisibility: (enabled:boolean) => void;
  setDarkMode: (darkMode:boolean|undefined) => void;
}

type DownloadKind = 'SCHEDULE' | 'ECONOMY' | 'TODOS';

const darkModeOption = (darkMode:boolean|undefined) => darkMode === true ? "DARK" : darkMode === false ? "LIGHT" : "PHONE";

interface IState {
  inviteDialogOpen: boolean;
  updateDialogOpen: boolean;
  addFamilyMemberDialogOpen: boolean;
  addFamilyMemberType: FamilyMemberType;
  changePasswordDialogOpen: boolean;
  updateEmailDialogOpen: boolean;
  eraseAccountDialogOpen: boolean;
  veryStrictScreeningDialogOpen: boolean;
  autoApproveSuggestionsDialogOpen: boolean;
  deleteGroupDialogOpen: boolean;
  // Downloads requested since the screen was opened; each can be sent once per visit.
  downloadsSent: DownloadKind[];
  newFamilyDialogOpen: boolean;
  importantViewerId: number | null;
  currentId: number;
  inviteUser: IFamilyMember|undefined;
  coppaBlockedChild: IFamilyMember|undefined;
  disableAnotherFamily: boolean;
}



interface IRouteParams {
  userId?: string;
}

class PSettings extends React.Component<IProps, IState> {
  public state = {
    inviteDialogOpen: false,
    updateDialogOpen: false,
    addFamilyMemberDialogOpen: false,
    changePasswordDialogOpen: false,
    updateEmailDialogOpen: false,
    eraseAccountDialogOpen: false,
    veryStrictScreeningDialogOpen: false,
    autoApproveSuggestionsDialogOpen: false,
    deleteGroupDialogOpen: false,
    downloadsSent: [] as DownloadKind[],
    newFamilyDialogOpen: false,
    shareEventsDialogOpen: false,
    importantViewerId: null,
    currentId: -1,
    addFamilyMemberType: FamilyMemberType.VIEWER,
    inviteUser:undefined,
    coppaBlockedChild:undefined,
    disableAnotherFamily: false,
  };
  private emailActivationSequence = 0;

  public componentDidMount() {
    const { userId } = this.props.match.params as IRouteParams;
    this.checkExternallyGivenUserId(userId);
  }

  public componentDidUpdate(oldProps:IProps) {
    const { userId } = this.props.match.params as IRouteParams;
    if (userId) {
      const params  = oldProps.match.params as IRouteParams;
      if (!params.userId || params.userId !== userId) {
        this.checkExternallyGivenUserId(userId);
      }
    }
    if(oldProps.family && this.props.family && oldProps.family.length !== this.props.family.length && oldProps.family.length !== 0){
      const lastIndex = this.props.family.length -1;
      const lastFamMember = this.props.family[lastIndex];
      if(lastFamMember.role === FamilyMemberType.VIEWER){
        this.handleOpenShareEventDialog(lastFamMember.id)
      } 
    }
  }

  
  public render() {
    const classes = this.props.classes;
    const mainTextStyle = this.props.enhancedVisibility ? { fontSize: '26px' } : undefined;
    const allowCalls = this.props.calendarSettings.allowCalls !== false;
    let adminCard:JSX.Element = <div />;

    if ((this.props.session?.data?.resources && this.props.session.data.resources.length > 1 ) 
      || this.props.userRole === FamilyMemberType.PARENT) {
      adminCard = 
        <div className='settingsCard'>
          { (this.props.session?.data?.resources && this.props.session.data.resources.length > 1 ) &&
            <p className={classes.mainText} style={mainTextStyle}>{tn.lang.switchGroup}</p>}
          <FamilySwitch
            session={this.props.session}
            highlight={this.state.disableAnotherFamily}
            selectFamily={(index:number) => {this.props.selectFamily(index); this.props.clearChat(true);}}
            reloadResources={this.props.reloadResources}
          />
          <Button size="small"
            variant="contained"
            color="primary"
            disabled={
              (!(this.props.userRole === FamilyMemberType.PARENT || this.props.userRole === FamilyMemberType.VIEWER))
              || this.state.disableAnotherFamily}
            onClick={() => this.setState({newFamilyDialogOpen:true})}
          >
            {tn.lang.createNewFamily}
          </Button>
          {this.canDeleteGroup() &&
            <Button size="small"
              variant="outlined"
              color="secondary"
              onClick={() => this.setState({deleteGroupDialogOpen:true})}
            >
              {tn.lang.deleteGroup}
            </Button>}
        </div>
    }


    const addOthersButtons:JSX.Element[] = this.props.extendedFamily.map((emem) => <Button size="small" 
        variant="outlined" key = {emem.id}
        color="primary"
        disabled={!(this.props.userRole === FamilyMemberType.PARENT
          || this.props.userRole === FamilyMemberType.MODERATOR)}
        onClick={() => this.setState({inviteUser:emem})}
      >
        {tn.lang.addUserAsView(emem.firstName)}
      </Button>);

    return (
      <div className={classes.container}>
        <div className={this.props.classes.containerTop}>
          <div className={this.props.classes.containerCorners} />
        </div>
        {this.state.inviteDialogOpen ?
          <InviteParentDialog
            familyMember={this.getFamilyMember(this.state.currentId)}
            onClose={this.handleCloseDialog}
            isOpen={this.state.inviteDialogOpen}
          /> : null
        }

        <CoppaInviteBlockedDialog
          isOpen={this.state.coppaBlockedChild !== undefined}
          child={this.state.coppaBlockedChild}
          onClose={() => this.setState({coppaBlockedChild:undefined})}
        />

        <AddFamilyMemberDialog
          isOpen={this.state.addFamilyMemberDialogOpen}
          role={this.state.addFamilyMemberType}
          onClose={this.handleCloseDialog}
        />
        
        <AddUserDialog
          isOpen={this.state.inviteUser !== undefined}
          member={this.state.inviteUser}
          onClose={() => this.setState({inviteUser:undefined})}
        />
        
        <NewFamilyDialog
          isOpen={this.state.newFamilyDialogOpen}
          onClose={this.handleCloseDialog}
        />
        
        {this.state.updateDialogOpen ?
          <UpdateFamilyMemberDialog
            familyMember={this.getFamilyMember(this.state.currentId)}
            isOpen={this.state.updateDialogOpen}
            onClose={this.handleCloseDialog}
            onSubmit={this.handleUpdateFamilyMember}
            onSaveFile={(data) => this.props.updateFamilyMemberFileId(data)}
          /> : null
        }
        {this.state.changePasswordDialogOpen ?
          <UpdatePasswordDialog
            isOpen={this.state.changePasswordDialogOpen}
            onClose={this.handleCloseDialog}
            onSubmit={this.handleChangePassword}
          /> : null
        }
        {this.state.updateEmailDialogOpen ?
          <UpdateEmailDialog
            isOpen={this.state.updateEmailDialogOpen}
            onClose={this.handleCloseDialog}
            onSubmit={this.handleUpdateEmail}
            /> : null
        }
        {this.state.eraseAccountDialogOpen ?
          <EraseAccountDialog
            isOpen={this.state.eraseAccountDialogOpen}
            onClose={this.handleCloseDialog}
            onSubmit={this.handleEraseAccount}
          /> : null
        }
        <ConfirmSettingDialog
          isOpen={this.state.veryStrictScreeningDialogOpen}
          title={tn.lang.veryStrictScreeningTitle}
          paragraphs={[tn.lang.veryStrictScreeningWarning]}
          confirmLabel={tn.lang.veryStrictScreeningConfirm}
          onClose={this.handleCloseDialog}
          onSubmit={this.handleConfirmVeryStrictScreening}
        />
        <ConfirmSettingDialog
          isOpen={this.state.autoApproveSuggestionsDialogOpen}
          title={tn.lang.autoApproveSuggestionsTitle}
          paragraphs={[tn.lang.autoApproveSuggestionsWarning]}
          confirmLabel={tn.lang.autoApproveSuggestionsConfirm}
          onClose={this.handleCloseDialog}
          onSubmit={this.handleConfirmAutoApproveSuggestions}
        />
        <ConfirmSettingDialog
          isOpen={this.state.deleteGroupDialogOpen}
          title={tn.lang.deleteGroupTitle}
          paragraphs={[tn.lang.deleteGroupWarning(getSelectedFamilyResource(this.props.session)?.familyName || "")]}
          confirmLabel={tn.lang.deleteGroup}
          onClose={this.handleCloseDialog}
          onSubmit={this.handleConfirmDeleteGroup}
        />
        <ShareEventDialog
          importantViewerId={this.state.importantViewerId} 
          onClose={() => this.setState({ importantViewerId: null })}
        />
        <div style={{maxHeight:'100vh', overflowY:'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'auto'}}>
          <div className={`settingsPanel${this.props.enhancedVisibility ? ' settingsPanelEnhancedVisibility' : ''}`}>

            <div className='settingsCard'>
              <p className={classes.mainText} style={mainTextStyle} >
                {tn.lang.extendedFamily}
              </p>
              <FamilyList
                family={this.props.family}
                myID={this.props.userId}
                myRole={this.props.userRole}
                updateFamilyColors={(usId, c) => this.updateMemberColor(usId, c)}
                onInvite={(id) => this.handleInvite(id)}
                onDelete={(id) => this.handleDelete(id)}
                onUnlimit={(id) => this.props.unlimitFamilyMember(id)}
                onRestore={(id) => this.handleRestoreAccount(id)}
                onNudgeToLogIn={(id) => this.handleNudgeToLogIn(id)}
                onUpdate={(id) => this.handleUpdate(id)}
              />
              <Button size="small"
                variant="contained"
                color="primary"
                disabled={!(this.props.userRole === FamilyMemberType.PARENT
                  || this.props.userRole === FamilyMemberType.MODERATOR)}
                onClick={this.handleOpenAddChildDialog}
              >
                {tn.lang.addChild}
              </Button>

              <Button size="small"
                variant="contained"
                color="primary"
                disabled={!(this.props.userRole === FamilyMemberType.PARENT
                  || this.props.userRole === FamilyMemberType.MODERATOR)}
                onClick={this.handleOpenAddViewerDialog}
              >
                {tn.lang.addViewer}
              </Button>

              {addOthersButtons}

              <Button size="small"
                variant="contained"
                color="primary"
                style={{
                  display: (this.props.userRole === FamilyMemberType.MODERATOR
                    ?
                    "initial"
                    :
                    "none"),
                }}
                disabled={!(this.props.userRole === FamilyMemberType.MODERATOR)}
                onClick={this.handleOpenAddParentDialog}
              >
                {tn.lang.addParent}
              </Button>
            </div>

            {adminCard}

            <div className='settingsCard'>
              <p className={classes.mainText} style={mainTextStyle} >
                {tn.lang.account}
              </p>
              <div className={classes.acccountContainer}>
                <div className={classes.accountItem} onClick={this.handleLogout}>
                  <OffSvg />
                  {tn.lang.logout}
                </div>
                <div className={classes.accountItem}
                  onClick={this.handleOpenChangePasswordDialog}>
                  <LockSvg />
                  {tn.lang.changePassword}
                </div>
                <div className={classes.accountItem}
                  onClick={this.handleOpenUpdateEmailDialog}>
                  <LockSvg />
                  {tn.lang.changeEmail}
                </div>
                <div className={classes.accountItem} onClick={() => this.setState({eraseAccountDialogOpen: true})}>
                  <OffSvg />
                  {tn.lang.eraseAccount}
                </div>
              </div>
            </div>

            <div className='settingsCard'>
              <p className={classes.mainText} style={mainTextStyle} >
                {tn.lang.settingsChat}
              </p>
              <div className={classes.acccountContainer}>
                <div className={classes.accountItem} >
                  <FormControlLabel 
                    onClick={(event:any) => this.props.allowCalls(!allowCalls)}
                    control={<Switch color="primary" checked={allowCalls} readOnly={true}/>} 
                    label={tn.lang.allowCalls} />
                </div>
                <div className={classes.screeningSetting}>
                  <div className={classes.screeningLabel}>
                    <Typography id="message-screening-label">{tn.lang.messageScreening}</Typography>
                    <IconButton
                      className={classes.filterHelpButton}
                      aria-label={tn.lang.strictToxicFilterHelp}
                      title={tn.lang.strictToxicFilterHelp}
                      onClick={() => this.props.openArticle({reference: '8203'})}
                    >
                      <HelpOutline />
                    </IconButton>
                  </div>
                  <Select
                    value={this.props.toxicCheckLevel}
                    onChange={this.handleToxicCheckLevelChange}
                    labelId="message-screening-label"
                    className={classes.screeningSelect}
                  >
                    <MenuItem value={TOXIC_CHECK_LEVEL.NORMAL}>{tn.lang.messageScreeningNormal}</MenuItem>
                    <MenuItem value={TOXIC_CHECK_LEVEL.STRICT}>{tn.lang.messageScreeningStrict}</MenuItem>
                    <MenuItem value={TOXIC_CHECK_LEVEL.VERY_STRICT}>{tn.lang.messageScreeningVeryStrict}</MenuItem>
                  </Select>
                </div>
                <div className={classes.accountItem} >
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={this.props.disablePreviews}
                        onChange={this.handleDisablePreviewsChange}
                      />
                    }
                    label={tn.lang.disablePreviews}
                  />
                </div>
              </div>
            </div>

            {SHOW_EMAIL_SETTINGS && (
              <EmailIntegrationSettings
                accountId={this.props.userId}
                copy={this.emailIntegrationCopy()}
                familyId={getSelectedFamilyResource(this.props.session)?.id}
                address={getForwardingEmailAddress(this.props.session, this.props.calendarSettings)}
                onActivate={this.activateEmailAddress}
                activationPending={this.props.emailActivation?.pending}
                activationFailed={this.props.emailActivation?.failed}
              />
            )}

            <div className='settingsCard'>
              <p className={classes.mainText} style={mainTextStyle} >
                {tn.lang.preferences}
              </p>
              <div className={classes.acccountContainer}>
                <div className={classes.accountItem}>
                  <LanguageSvg />
                  <SelectLanguage />
                </div>
                <div className={classes.accountItem} >
                  <FormControlLabel 
                    onClick={(event:any) => this.updateNotifications("HANDOVER")}
                    control={<Switch color="primary" checked={(this.props.calendarSettings.notifyHandovers)}/>} 
                    label={tn.lang.notifyHandover} />
                </div>
                <div className={classes.accountItem} >
                  <FormControlLabel 
                    onClick={(event:any) => this.updateNotifications("ACTIVITY")}
                    control={<Switch color="primary" checked={(this.props.calendarSettings.notifyActivities)}/>} 
                    label={tn.lang.notifyMorningSummary} />
                </div>
                <div className={classes.accountItem} >
                  <FormControlLabel
                    onClick={(event:any) => this.updateNotifications("LISTS")}
                    control={<Switch color="primary" checked={this.props.calendarSettings.muteListNotifications !== true} />}
                    label={tn.lang.muteListNotifications} />
                </div>
                <div className={classes.accountItem} >
                  <FormControlLabel 
                    onClick={(event:any) => this.updateNotifications("EMAIL")}
                    control={<Switch color="primary" checked={(this.props.calendarSettings.emailMessages?true:false)} />} 
                    label={tn.lang.emailMissedMessages} />
                </div>
                {this.props.userRole === FamilyMemberType.PARENT && (
                  <div className={classes.accountItem} >
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={this.props.autoApproveSuggestions}
                          onChange={this.handleAutoApproveSuggestionsChange}
                        />
                      }
                      label={tn.lang.autoApproveSuggestions}
                    />
                  </div>
                )}
                <div className={classes.accountItem} >
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={this.props.enhancedVisibility === true}
                        onChange={this.handleEnhancedVisibilityChange}
                      />
                    }
                    label={tn.lang.enhancedVisibility}
                  />
                </div>
                <div className={classes.accountItem} >
                  <Typography>{tn.lang.darkMode}</Typography>
                  <Select
                    value={darkModeOption(this.props.darkMode)}
                    onChange={this.handleDarkModeChange}
                    inputProps={{'aria-label': tn.lang.darkMode}}
                  >
                    <MenuItem value="PHONE">{tn.lang.darkModeSameAsPhone}</MenuItem>
                    <MenuItem value="DARK">{tn.lang.darkModeAlwaysDark}</MenuItem>
                    <MenuItem value="LIGHT">{tn.lang.darkModeAlwaysLight}</MenuItem>
                  </Select>
                </div>
              </div>
            </div>

            <div className='settingsCard' style={{wordBreak: 'break-all', display: 'flex', flexDirection: 'column'}}>
              <SubscriptionSettings />
            </div>

            <div className='settingsCard'>
              <p className={classes.mainText} style={mainTextStyle} >
                {tn.lang.settingsDownload}
              </p>
              <div className={classes.downloadButtons}>
                <Button size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<GetApp />}
                  disabled={this.props.scheduleChatId === undefined || this.downloadSent('SCHEDULE')}
                  onClick={this.handleDownloadScheduleChanges}
                >
                  {tn.lang.downloadScheduleChanges}
                </Button>
                <Button size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<GetApp />}
                  disabled={this.downloadSent('ECONOMY')}
                  onClick={this.handleDownloadEconomy}
                >
                  {tn.lang.downloadEconomy}
                </Button>
                <Button size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<GetApp />}
                  disabled={this.downloadSent('TODOS')}
                  onClick={this.handleDownloadTodos}
                >
                  {tn.lang.downloadTodos}
                </Button>
              </div>
            </div>

          </div>
        </div>
        <BottomBar
          hidden={false}
          displaySubmenyCallback={(hidden:boolean) => {return;}}
        />
      </div>
    );
  }

  private updateMemberColor(userId:number, color:string) {
    this.props.updateFamilyColors(getFamilyColors(this.props.family, {userId, color}));
  }

  private emailIntegrationCopy(): IEmailIntegrationSettingsCopy {
    return {
      heading: tn.lang.gmailSettingsHeading,
      explanation: tn.lang.gmailSettingsExplanation,
      activate: tn.lang.emailIntegrationActivate,
      activating: tn.lang.emailIntegrationActivating,
      activationError: tn.lang.emailIntegrationActivationError,
      forwarding: tn.lang.emailIntegrationForwardTo,
      copyAddress: tn.lang.copy,
      copied: tn.lang.emailIntegrationCopied,
      copyError: tn.lang.emailIntegrationCopyError,
      addContact: tn.lang.emailIntegrationAddContact,
      openingContacts: tn.lang.emailIntegrationOpeningContacts,
      contactName: tn.lang.emailIntegrationContactName,
      contactError: tn.lang.emailIntegrationContactError,
      loading: tn.lang.loading,
      unavailable: tn.lang.emailIntegrationUnavailable,
      noFamily: tn.lang.gmailNoAuthorizedFamilies,
    };
  }

  private activateEmailAddress = () => {
    const family = getSelectedFamilyResource(this.props.session);
    if (!family || getForwardingEmailAddress(this.props.session, this.props.calendarSettings) !== null
      || this.props.emailActivation?.pending) { return; }
    this.props.activateEmailAddress({
      accountId: this.props.userId,
      familyId: family.id,
      requestId: `EMAIL_ACTIVATE:${Date.now()}:${++this.emailActivationSequence}`,
    });
  };

  private checkExternallyGivenUserId(userId:any) {
    if (userId) {
      const userIdNumber = Number(userId);
      if (userIdNumber) {
        for (const mem of this.props.family) {
          if (mem.id === userIdNumber) {
            if (this.isCoppaBlocked(mem)) {
              this.setState({currentId:userIdNumber, coppaBlockedChild:mem});
            } else {
              this.setState({currentId:userIdNumber, inviteDialogOpen:true});
            }
          }
        }
      }
    }
  }

  private getFamilyMember = (id: number): IFamilyMember => {
    if (!this.props.family) {
      return {
        lastName: "unknwon", firstName: "unknown", lastOnline: 0, avatar: 0, color:"#000",
        sex: "UNKNOWN", role: FamilyMemberType.VIEWER, online: false, inviteEmail: "", id: 0, inviteTime: 0
      };
    }
    for (const member of this.props.family) {
      if (member.id === id) {
        return member;
      }
    }
    return {
      lastName: "unknwon", firstName: "unknown", lastOnline: 0, avatar: 0, color:"#000",
      sex: "UNKNOWN", role: FamilyMemberType.VIEWER, online: false, inviteEmail: "", id: 0, inviteTime: 0
    };
  }

  private handleLogout = async () => {
    this.props.logout();
  }

  private handleChangePassword = (value: string) => {
    this.props.changePassword(value);
    this.handleCloseDialog();
  }

  private handleUpdateEmail = (value: string) => {
    this.props.updateEmail(value);
    this.handleCloseDialog();
  }

  private handleEraseAccount = () => {
    this.props.eraseAccount();
    this.handleCloseDialog();
    this.handleLogout();
  }

  private handleUpdateFamilyMember = (value: IFamilyMember) => {
    this.props.updateFamilyMember(value);
    this.handleCloseDialog();
  }

  private handleOpenInviteDialog = () => {
    this.setState({
      inviteDialogOpen: true
    });
  }

  private handleOpenUpdateDialog = () => {
    this.setState({
      updateDialogOpen: true
    });
  }

  private handleOpenChangePasswordDialog = () => {
    this.setState({
      changePasswordDialogOpen: true
    });
  }

  
  private handleOpenUpdateEmailDialog = () => {
    this.setState({
      updateEmailDialogOpen: true
    });
  }

  private handleOpenNewFamilyDialog = () => {
    this.setState({
      newFamilyDialogOpen: true
    });
  }

  private handleOpenAddChildDialog = () => {
    this.setState({
      addFamilyMemberDialogOpen: true,
      addFamilyMemberType: FamilyMemberType.CHILD
    });
  }
  private handleOpenAddViewerDialog = () => {
    this.setState({
      addFamilyMemberDialogOpen: true,
      addFamilyMemberType: FamilyMemberType.VIEWER
    });
  }

  private handleOpenAddParentDialog = () => {
    this.setState({
      addFamilyMemberDialogOpen: true,
      addFamilyMemberType: FamilyMemberType.PARENT
    });
  }

  private handleOpenShareEventDialog = (id: number) => {
    if (id === null) {
      return;
    };
    this.setState({
      importantViewerId: id,
    });
  };
  
  private handleCloseDialog = (disableCreateNewFamily?:boolean) => {
    this.setState({
      inviteDialogOpen: false,
      addFamilyMemberDialogOpen: false,
      updateDialogOpen: false,
      changePasswordDialogOpen: false,
      updateEmailDialogOpen: false,
      newFamilyDialogOpen: false,
      eraseAccountDialogOpen: false,
      veryStrictScreeningDialogOpen: false,
      autoApproveSuggestionsDialogOpen: false,
      deleteGroupDialogOpen: false,
      importantViewerId: null,
      coppaBlockedChild: undefined,
      disableAnotherFamily: (disableCreateNewFamily===true?true:false),
    });
  }

  private updateNotifications(key:"HANDOVER"|"ACTIVITY"|"LISTS"|"EMAIL") {
    const newSettings:ICalendarSetting = {
      ...this.props.calendarSettings,
      notifyActivities:((key==="ACTIVITY")?(!this.props.calendarSettings.notifyActivities)
        :this.props.calendarSettings.notifyActivities),
      notifyHandovers:((key==="HANDOVER")?(!this.props.calendarSettings.notifyHandovers)
        :this.props.calendarSettings.notifyHandovers),
      muteListNotifications:((key==="LISTS")?(!this.props.calendarSettings.muteListNotifications)
        :this.props.calendarSettings.muteListNotifications),
      emailMessages:((key==="EMAIL")?(!this.props.calendarSettings.emailMessages)
        :this.props.calendarSettings.emailMessages),
    };
    this.props.updateCalendarSettings(newSettings);
  }

  private handleEnhancedVisibilityChange = () => {
    this.props.setEnhancedVisibility(!this.props.enhancedVisibility);
  }

  private handleToxicCheckLevelChange = (event: React.ChangeEvent<{value: unknown}>) => {
    const level = Number(event.target.value);
    if (level === TOXIC_CHECK_LEVEL.VERY_STRICT) {
      this.setState({veryStrictScreeningDialogOpen: true});
      return;
    }
    this.props.updateToxicCheckLevel(level);
  }

  private handleConfirmVeryStrictScreening = () => {
    this.props.updateToxicCheckLevel(TOXIC_CHECK_LEVEL.VERY_STRICT);
    this.handleCloseDialog();
  }

  // Not optimistic: the toggle follows the server's reply, which also reaches the parent's other devices.
  private handleAutoApproveSuggestionsChange = () => {
    if (this.props.autoApproveSuggestions) {
      this.sendAutoApproveSuggestions(false);
    } else {
      this.setState({autoApproveSuggestionsDialogOpen: true});
    }
  }

  // Not optimistic: the switch follows the familyUpdate the server sends back to all of the member's devices.
  private handleDisablePreviewsChange = () => {
    const family = getSelectedFamilyResource(this.props.session);
    if (!family) { return; }
    this.props.setDisablePreviews({familyId: family.id, disabled: !this.props.disablePreviews});
  }

  private handleConfirmAutoApproveSuggestions = () => {
    this.sendAutoApproveSuggestions(true);
    this.handleCloseDialog();
  }

  // Only offered when there is another group to switch to and nobody else was ever invited to this one.
  private canDeleteGroup(): boolean {
    return this.props.resources.length > 1
      && (this.props.userRole === FamilyMemberType.PARENT || this.props.userRole === FamilyMemberType.MODERATOR)
      && isGroupOnlyMine(this.props.family, this.props.userId);
  }

  private handleConfirmDeleteGroup = () => {
    const family = getSelectedFamilyResource(this.props.session);
    this.handleCloseDialog();
    if (!family) { return; }
    this.props.deleteGroup({accountId: this.props.userId, familyId: family.id, requestId: `DELETE_GROUP:${Date.now()}`});
  }

  private sendAutoApproveSuggestions(enabled: boolean) {
    const family = getSelectedFamilyResource(this.props.session);
    if (!family) { return; }
    this.props.setAutoApproveSuggestions({familyId: family.id, enabled});
  }

  // The PDFs are emailed, the same as the downloads in the chat and economy views.
  private handleDownloadScheduleChanges = () => {
    if (this.props.scheduleChatId === undefined) { return; }
    if (!this.props.isOnline) {
      this.props.displayErrorMessage(tn.lang.ecoExportOffline);
      return;
    }
    this.props.saveChat(this.props.scheduleChatId);
    this.markDownloadSent('SCHEDULE');
    this.props.displaySuccessMessage(tn.lang.chatHistorySaved);
  }

  private handleDownloadEconomy = () => {
    if (saveEconomy()) {
      this.markDownloadSent('ECONOMY');
      this.props.displaySuccessMessage(tn.lang.ecoExportEmailQueued);
    } else { this.props.displayErrorMessage(tn.lang.ecoExportOffline); }
  }

  private handleDownloadTodos = () => {
    if (saveTodos()) {
      this.markDownloadSent('TODOS');
      this.props.displaySuccessMessage(tn.lang.todosExportEmailQueued);
    } else { this.props.displayErrorMessage(tn.lang.ecoExportOffline); }
  }

  private downloadSent(kind: DownloadKind): boolean {
    return this.state.downloadsSent.indexOf(kind) >= 0;
  }

  // Only after the request went out, so a download that failed offline can be tried again.
  private markDownloadSent(kind: DownloadKind) {
    this.setState((state) => ({downloadsSent: [...state.downloadsSent, kind]}));
  }

  private handleDarkModeChange = (event: React.ChangeEvent<{value: unknown}>) => {
    const option = event.target.value;
    this.props.setDarkMode(option === "DARK" ? true : option === "LIGHT" ? false : undefined);
  }

  private handleUpdate = (id: number ) => {
    this.setState({currentId: id});
    this.handleOpenUpdateDialog();
  }

  private handleDelete = (id: number ) => {
    this.setState({currentId: id});
    this.props.deleteFamilyMember(id);
  }

  private handleRestoreAccount = (id: number ) => {
    this.setState({currentId: id});
    this.props.restoreAccount(id);
  }

  private handleNudgeToLogIn = (id: number ) => {
    this.setState({currentId: id});
    this.props.nudgeToLogIn(id);
  }

  private handleInvite = (id: number ) => {
    const member = this.getFamilyMember(id);
    if (this.isCoppaBlocked(member)) {
      this.setState({currentId: id, coppaBlockedChild: member});
      return;
    }
    this.setState({currentId: id});
    this.handleOpenInviteDialog();
  }

  /**
   * A child who is not provably 13 cannot be invited from a US time zone, so the invite
   * dialog is replaced by a notice explaining COPPA. Checked at every entry point into it.
   */
  private isCoppaBlocked = (member: IFamilyMember): boolean => {
    return isCoppaBlockedInvite(member);
  }

}

// The family's Schemaändringar thread. Older families may also have one per schedule; the shared one has no reference.
function scheduleChatIdOf(chats: IChat[] | undefined): number | undefined {
  const scheduleChats = (chats || []).filter((chat) => chat.chatType === ChatType.SCHEDULE_CHAT);
  const shared = scheduleChats.find((chat) => !chat.referenceObject) || scheduleChats[0];
  return shared?.id;
}

function mapStateToProps(state: any) {
  const userId = state.user.session.data.id;
  const resources = Array.isArray(state.user.session.data.resources)
    ? state.user.session.data.resources
    : [];
  const family: IFamilyMember[] = state.family.family;
  let userRole = FamilyMemberType.VIEWER;
  for (const mem of family) {
    if (mem.id === userId) {
      userRole = mem.role;
    }
  }

  let session: any;
  if (state.user && state.user.session) {
    session = state.user.session;
  }
  const extendedFamily:IFamilyMember[] = [];
  if (userRole === FamilyMemberType.PARENT && state.user.session?.data?.resources && state.user.session.data.resources.length > 1) {
    if (state.user.session.extendedFamily) {
      for (const eFam of state.user.session.extendedFamily) {
        if (eFam.id === userId) { continue;}
        if (family.findIndex((mem) => mem.id === eFam.id) !== -1) {continue;}
        if (extendedFamily.findIndex((mem) => mem.id === eFam.id) !== -1) {continue;}
        if (eFam.role === FamilyMemberType.DELETED || eFam.role === FamilyMemberType.EXPERT || eFam.role === FamilyMemberType.MODERATOR) {
          continue;
        }
        extendedFamily.push(eFam);
      }
    }
  }

  return {
    session,
    userRole,
    family,
    resources,
    userId,
    calendarSettings: state.user.calendarSettings,
    extendedFamily,
    toxicCheckLevel: toxicCheckLevelOf(state.user),
    autoApproveSuggestions: state.user.autoApproveSuggestions === true,
    disablePreviews: previewsDisabledFor(family, userId),
    scheduleChatId: scheduleChatIdOf(state.chat.chats),
    isOnline: !!state?.connection?.loadCompleted,
    enhancedVisibility: state.user.enhancedVisibility || false,
    darkMode: typeof state.user.darkMode === 'boolean' ? state.user.darkMode : undefined,
    language: state.user.language || 'EN',
    emailActivation: state.connection.emailActivation?.accountId === userId
      && state.connection.emailActivation?.familyId === getSelectedFamilyResource(state.user.session)?.id
      ? state.connection.emailActivation : undefined,
  };
}


const mapDispatchToProps = {
  updateFamilyMember: FamilyActions.updateFamilyMember,
  updateFamilyMemberFileId: FamilyActions.updateFamilyMemberFileId,
  deleteFamilyMember: FamilyActions.deleteFamilyMember,
  unlimitFamilyMember: FamilyActions.unlimitFamilyMember,
  restoreAccount: FamilyActions.restoreAccount,
  nudgeToLogIn: FamilyActions.nudgeToLogIn,
  changePassword: UserActions.changePassword,
  updateEmail: UserActions.updateEmail,
  eraseAccount: UserActions.eraseAccount,
  logout: UserActions.logout,
  selectFamily: UserActions.selectFamily,
  deleteGroup: UserActions.deleteGroup,
  updateFamilyColors: UserActions.updateFamilyColors,
  clearChat: ChatActions.clearChat,
  reloadResources: UserActions.reloadResources,
  activateEmailAddress: UserActions.activateEmailAddress,
  updateCalendarSettings:UserActions.updateCalendarSettings,
  createFamily: FamilyActions.createFamily,
  allowCalls:CallsActions.allowCalls,
  updateToxicCheckLevel: UserActions.updateToxicCheckLevel,
  setDisablePreviews: UserActions.setDisablePreviews,
  setAutoApproveSuggestions: UserActions.setAutoApproveSuggestions,
  saveChat: ChatActions.saveChat,
  displaySuccessMessage: UserActions.displaySuccessMessage,
  displayErrorMessage: UserActions.displayErrorMessage,
  openArticle,
  setEnhancedVisibility: UserActions.setEnhancedVisibility,
  setDarkMode: UserActions.setDarkMode,
};

const Settings = withRouter(connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(PSettings)));

export default Settings;



// WEBPACK FOOTER //
// ./src/containers/settings/settings.container.tsx